import React, {useMemo, useState} from 'react';
import {useFormikContext} from 'formik';
import {
    Card, Chip, Skeleton,
    Typography
} from "@mui/material";
import {DragDropContext, Droppable, Draggable} from "react-beautiful-dnd";
import Grid from "@mui/material/Grid2";
import {useGetPatrimoineDetailQuery} from "../../../../modules/patrimoines/patrimoines.api";
import {extractId, sameResource} from "../../../../utils/apiResource";


function CampaignOrderField({...props}) {
    const {values} = props;
    const {setFieldValue} = useFormikContext();

    // Détail (avec conteneurs) — déjà en cache si l'utilisateur est passé par le step 2.
    const {
        data: selectedPatrimoine,
        isFetching: detailFetching,
    } = useGetPatrimoineDetailQuery(values.patrimoine, {
        skip: !values.patrimoine,
    });

    const findContainersByIri = (iris, patrimoine) => {
        if (patrimoine && patrimoine.conteneurs) {
            let containers = [];
            for (let iri of iris) {
                const container = patrimoine.conteneurs.find((c) => sameResource(c, iri));
                if (container) {
                    containers.push(container);
                }
            }

            if (values.ordre?.length > 0 && values.ordre.length === containers.length) {
                containers = [...containers].sort((a, b) => {
                    const indexA = values.ordre.findIndex((id) => String(id) === String(extractId(a)));
                    const indexB = values.ordre.findIndex((id) => String(id) === String(extractId(b)));
                    return indexA - indexB;
                });
            } else {
                containers = [...containers].sort((a, b) => {
                    return parseInt(a.ordreCsv) - parseInt(b.ordreCsv);
                });
            }
            return containers;
        }
        return [];
    }


    // Recalcule dynamiquement la liste ordonnée des conteneurs sélectionnés
    // à partir du détail récupéré (mise à jour automatique au chargement RTK Query).
    const computedConteneurs = useMemo(
        () => (values.conteneurs?.length > 0
            ? findContainersByIri(values.conteneurs, selectedPatrimoine)
            : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedPatrimoine, values.conteneurs, values.ordre],
    );
    const [selectedConteneurs, setSelectedConteneurs] = useState(computedConteneurs);

    // Resync local state quand le détail arrive (asynchrone)
    React.useEffect(() => {
        setSelectedConteneurs(computedConteneurs);
    }, [computedConteneurs]);

    const onDragEnd = (result) => {
        if (!result.destination) {
            return;
        }
        const reorderedContainers = reorder(
            selectedConteneurs,
            result.source.index,
            result.destination.index
        );

        const reorderedIds = reorderedContainers.map(c => {
            const id = extractId(c);
            const numeric = Number(id);
            return Number.isNaN(numeric) ? id : numeric;
        });

        setFieldValue("ordre", reorderedIds);

        setSelectedConteneurs(reorderedContainers);
    };

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const getCardStyle = (isDragging, draggableStyle) => ({
        userSelect: "none",
        padding: "16px",
        margin: "0 0 8px 0",
        background: isDragging ? "var(--thm-base)" : "white",
        borderRadius: "4px",
        paddingLeft: '2rem',
        boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
        ...draggableStyle
    });

    const getChipStyle = (isDragging) => {
        return {
            background: isDragging ? "var(--thm-black)" : "var(--thm-black)",
            color: "white",
            marginRight: "1rem"
        }
    }

    return (
        <Grid container size={12} spacing={1} className={"sectionContainer"}>
            <Grid size={12}>
                <Typography variant={"h5"} className={"sectionHeader"}>Gestion de l'ordre de la tournée</Typography>
            </Grid>

            <Grid container className={"mt-2"}>
                <Grid size={12}>
                    {detailFetching && (
                        <Skeleton variant="rectangular" height={200}/>
                    )}
                    {selectedPatrimoine && !detailFetching &&
                        <Typography sx={{fontStyle: 'italic', textAlign: 'center'}}>
                            {selectedPatrimoine.isUnipatrimoine ? "L'ordre de la tournée n'est pas disponible pour un unipatrimoine" : "Ajustez l'ordre de la tournée en glissant/déposant les éléments dans la liste"}
                        </Typography>
                    }
                </Grid>
                {selectedPatrimoine &&
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="droppable">
                            {(provided) => (
                                <Grid container size={12}
                                      {...provided.droppableProps}
                                      ref={provided.innerRef}
                                >
                                    {selectedConteneurs.map((c, i) => {
                                            return (
                                                <Grid size={12} key={c['@id'] ?? c.id}>
                                                    <Draggable key={c['@id'] ?? c.id} draggableId={c['@id'] ?? c.id}
                                                               index={i}>
                                                        {(provided, snapshot) => (
                                                            <Card
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                style={getCardStyle(
                                                                    snapshot.isDragging,
                                                                    provided.draggableProps.style
                                                                )}
                                                            >
                                                                <Chip label={"Point numéro " + (i + 1)}
                                                                      style={getChipStyle(snapshot.isDragging)}/>
                                                                {c?.nomenclature}
                                                                &nbsp;-&nbsp;
                                                                {c?.adresse} - {c?.commune}
                                                            </Card>
                                                        )}
                                                    </Draggable>
                                                </Grid>
                                            )
                                        }
                                    )}
                                    {provided.placeholder}
                                </Grid>
                            )}
                        </Droppable>
                    </DragDropContext>
                }
            </Grid>
        </Grid>
    );
}

export default CampaignOrderField;
